// Renders the "Custom Fields" section inside TaskModal.
// One labeled input per field def, sorted by sort_order, bound to
// task.custom_values[def.key]. Edits fire patchTask({ custom_values }) on
// change/blur. Server 400 validation errors are already toasted by the api
// helper; this component additionally reverts the optimistic local value.

import { useState } from "preact/hooks";
import type { CustomFieldDef } from "@juuplan/shared";
import { INPUT_CLS } from "./NewProjectModal";
import { useUsers } from "../store/users";
import { patchTask } from "../store/project";

interface Props {
  taskId: number;
  /** Current custom_values from the task (read from server). */
  customValues: Record<string, unknown>;
  /** Sorted field definitions for this project. */
  fieldDefs: CustomFieldDef[];
  readOnly?: boolean;
}

/**
 * Renders a "Custom Fields" section with one input per field definition.
 * Values are patched to the server immediately on change/blur per field.
 * On a server error (400 validation) the api helper already toasts the message;
 * the local optimistic value is reverted to the last known good value.
 */
export function CustomFieldsInputs(props: Props): preact.JSX.Element | null {
  const { taskId, customValues, fieldDefs, readOnly = false } = props;
  const users = useUsers();

  // Local overrides while an edit is in-flight or before server confirms.
  const [localOverrides, setLocalOverrides] = useState<Record<string, unknown>>({});

  if (fieldDefs.length === 0) return null;

  /** Current effective value for a field: local override > server value > undefined */
  const getValue = (key: string): unknown => {
    if (key in localOverrides) return localOverrides[key];
    return customValues[key];
  };

  const persist = async (key: string, value: unknown): Promise<void> => {
    // Optimistic: store locally
    const prev = customValues[key];
    setLocalOverrides((o) => ({ ...o, [key]: value }));

    try {
      const nextValues = { ...customValues, ...localOverrides, [key]: value };
      await patchTask(taskId, { custom_values: nextValues });
      // On success, clear the local override (server value is now the source of truth)
      setLocalOverrides((o) => {
        const copy = { ...o };
        delete copy[key];
        return copy;
      });
    } catch {
      // api helper already toasted the error. Revert to previous value.
      setLocalOverrides((o) => {
        const copy = { ...o };
        if (prev === undefined) {
          delete copy[key];
        } else {
          copy[key] = prev;
        }
        return copy;
      });
    }
  };

  return (
    <section>
      <h3 class="text-xs font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)] mb-3">
        Eigene Felder
      </h3>
      <div class="flex flex-col gap-3">
        {fieldDefs.map((def) => (
          <FieldInput
            // Key includes the task: text/number inputs are uncontrolled
            // (defaultValue), so a reused instance would keep the PREVIOUS
            // task's value on screen when the modal switches tasks.
            key={`${taskId}:${def.id}`}
            def={def}
            value={getValue(def.key)}
            users={users.items}
            readOnly={readOnly}
            onCommit={(v) => void persist(def.key, v)}
          />
        ))}
      </div>
    </section>
  );
}

// ---------- per-type input ----------

interface FieldInputProps {
  def: CustomFieldDef;
  value: unknown;
  users: { id: number; name: string; email: string }[];
  readOnly: boolean;
  onCommit: (value: unknown) => void;
}

function FieldInput(props: FieldInputProps): preact.JSX.Element {
  const { def, value, users, readOnly, onCommit } = props;

  const labelNode = (
    <label class="text-label font-medium text-[color:var(--color-on-surface-variant)] flex items-center gap-1">
      {def.label}
      {def.required && (
        <span class="text-[color:var(--color-error)]" aria-label="Pflichtfeld">*</span>
      )}
    </label>
  );

  let inputNode: preact.JSX.Element;

  switch (def.type) {
    case "text":
    case "url": {
      const strVal = typeof value === "string" ? value : "";
      inputNode = (
        <input
          type={def.type === "url" ? "url" : "text"}
          defaultValue={strVal}
          readOnly={readOnly}
          disabled={readOnly}
          onBlur={(e) => {
            const v = (e.target as HTMLInputElement).value;
            if (v !== strVal) onCommit(v || null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            }
          }}
          class={`${INPUT_CLS} disabled:opacity-70 disabled:cursor-default`}
          aria-label={def.label}
        />
      );
      break;
    }

    case "number": {
      const numVal = value !== null && value !== undefined ? String(value) : "";
      inputNode = (
        <input
          type="number"
          defaultValue={numVal}
          readOnly={readOnly}
          disabled={readOnly}
          step="any"
          onBlur={(e) => {
            const raw = (e.target as HTMLInputElement).value;
            onCommit(raw === "" ? null : Number(raw));
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              (e.target as HTMLInputElement).blur();
            }
          }}
          class={`${INPUT_CLS} disabled:opacity-70 disabled:cursor-default`}
          aria-label={def.label}
        />
      );
      break;
    }

    case "date": {
      const dateVal = typeof value === "string" ? value : "";
      inputNode = (
        <input
          type="date"
          value={dateVal}
          readOnly={readOnly}
          disabled={readOnly}
          onChange={(e) => {
            const v = (e.target as HTMLInputElement).value;
            onCommit(v || null);
          }}
          class={`${INPUT_CLS} disabled:opacity-70 disabled:cursor-default`}
          aria-label={def.label}
        />
      );
      break;
    }

    case "checkbox": {
      const boolVal = value === true || value === "true";
      inputNode = (
        <label class="inline-flex items-center gap-2 text-sm text-[color:var(--color-on-surface)]">
          <input
            type="checkbox"
            checked={boolVal}
            disabled={readOnly}
            onChange={(e) => {
              onCommit((e.target as HTMLInputElement).checked);
            }}
            class="size-4 rounded border-[color:var(--color-outline)] text-[color:var(--color-primary)] focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 disabled:opacity-60"
            aria-label={def.label}
          />
          {boolVal ? "Ja" : "Nein"}
        </label>
      );
      break;
    }

    case "select": {
      const choices = def.options?.choices ?? [];
      const strVal = typeof value === "string" ? value : "";
      inputNode = (
        <select
          value={strVal}
          disabled={readOnly}
          onChange={(e) => {
            const v = (e.target as HTMLSelectElement).value;
            onCommit(v || null);
          }}
          class={`${INPUT_CLS} disabled:opacity-70 disabled:cursor-default`}
          aria-label={def.label}
        >
          <option value="">— Auswählen —</option>
          {choices.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      );
      break;
    }

    case "multiselect": {
      const choices = def.options?.choices ?? [];
      const selected: string[] = Array.isArray(value)
        ? (value as unknown[]).filter((v): v is string => typeof v === "string")
        : [];

      const toggle = (choice: string): void => {
        const next = selected.includes(choice)
          ? selected.filter((s) => s !== choice)
          : [...selected, choice];
        onCommit(next.length > 0 ? next : null);
      };

      inputNode = (
        <div class="flex flex-wrap gap-1" role="group" aria-label={def.label}>
          {choices.map((c) => {
            const active = selected.includes(c);
            return (
              <button
                key={c}
                type="button"
                disabled={readOnly}
                onClick={() => toggle(c)}
                class={`rounded-full px-2.5 py-0.5 text-xs font-medium border transition-colors disabled:cursor-default ${
                  active
                    ? "bg-[color:var(--color-primary)] text-[color:var(--color-on-primary)] border-[color:var(--color-primary)]"
                    : "bg-transparent text-[color:var(--color-on-surface-variant)] border-[color:var(--color-outline-variant)] hover:bg-[color:var(--color-surface-container-low)]"
                }`}
                aria-pressed={active}
              >
                {c}
              </button>
            );
          })}
          {choices.length === 0 && (
            <span class="text-xs text-[color:var(--color-on-surface-variant)] italic">
              Keine Optionen konfiguriert
            </span>
          )}
        </div>
      );
      break;
    }

    case "user": {
      const userId = typeof value === "number" ? value : null;
      inputNode = (
        <select
          value={userId !== null ? String(userId) : ""}
          disabled={readOnly}
          onChange={(e) => {
            const v = (e.target as HTMLSelectElement).value;
            onCommit(v ? Number(v) : null);
          }}
          class={`${INPUT_CLS} disabled:opacity-70 disabled:cursor-default`}
          aria-label={def.label}
        >
          <option value="">— Nicht zugewiesen —</option>
          {users.map((u) => (
            <option key={u.id} value={String(u.id)}>
              {u.name || u.email}
            </option>
          ))}
        </select>
      );
      break;
    }

    default: {
      inputNode = (
        <span class="text-xs text-[color:var(--color-on-surface-variant)] italic">
          Unbekannter Feldtyp
        </span>
      );
    }
  }

  return (
    <div class="flex flex-col gap-1">
      {labelNode}
      {inputNode}
    </div>
  );
}
